<script lang="ts" setup>

	import {type DeliveryDate, hasRoleMasterOrSalesRep} from 'dummen-shared';
	import {useI18n} from 'vue-i18n';
	import ShoppingcartWeekSelect from '@/components/ShoppingcartWeekSelect.vue';
	import CustomerSelect from '@/components/modals/purchaseordermodal/CustomerSelect.vue';

	const emit = defineEmits([
		'on-toggle-shopping-cart',
		'on-toggle-create-order-modal',
		'toggle-orderlines-with-availability-issues'
	]);

	defineProps<{
		deliveryDates: DeliveryDate[];
		canCreateOrder: boolean;
		showOrdersWithAvailabilityIssues: boolean;
		selectedCustomerId?: string | null;
	}>();

	const {t} = useI18n();

	const toggleShoppingCart = () => emit('on-toggle-shopping-cart');

	const toggleCreateOrderModal = () => emit('on-toggle-create-order-modal');

	const toggleFilterOrderlinesWithAvailabilityIssues = (event: Event) => {
		emit('toggle-orderlines-with-availability-issues', (event.target as HTMLInputElement).checked);
	};

</script>

<template>
	<div class="shoppingcart-header">

		<div class="shoppingcart-header__title">
			<h1>
				{{ t('shoppingcart.title.prefix') }} ({{ deliveryDates.length }})
			</h1>

			<button class="shoppingcart-header__close"
			        type="button"
			        @click.prevent="toggleShoppingCart">

				<svg class="icon-modal-close" height="20" width="20">
					<use xlink:href="/assets/images/build/svg-sprite.min.svg#modal-close"></use>
				</svg>
			</button>
		</div>

		<div class="shoppingcart-row">

			<div class="shoppingcart-row__selections">
				<div class="shoppingcart-header__week-select">
					<shoppingcart-week-select :delivery-dates="deliveryDates" />
				</div>

				<div v-if="hasRoleMasterOrSalesRep()" class="customer-selection">
					<CustomerSelect />
				</div>
			</div>

			<div class="shoppingcart-header__actions">
				<div class="shoppingcart-header__create">
					<button :disabled="!canCreateOrder"
					        class="button button--orange button--block button--small"
					        @click.prevent="toggleCreateOrderModal">

						{{ t('shoppingcart.create.button.text') }}
					</button>
				</div>

				<div class="shoppingcart-header__checkbox">
					<label class="filter-checkbox-label">
						<input :checked="showOrdersWithAvailabilityIssues"
						       type="checkbox"
						       @change="toggleFilterOrderlinesWithAvailabilityIssues" />

						<span class="filter-checkbox-icon" />

						<span class="filter-checkbox-text" style="display: inline">
							{{ t('shoppingcart.filterOrderWithInvalidAmount.text') }}
						</span>

						<svg class="icon-notice" height="16" style="margin-left: 6px" width="16">
							<use xlink:href="/assets/images/build/svg-sprite.catalog.min.svg#notice"></use>
						</svg>
					</label>
				</div>
			</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
	@use 'sass:math';
	@import '../../../../../../src/main/webapp/template/assets/css/source/settings';

	.shoppingcart-header__title {
		display: flex;
		justify-content: center;
		padding-bottom: 10px;

		h1 {
			margin: 0;
		}
	}

	.shoppingcart-row {
		display: flex;
		max-width: 1254px;
		margin: 0 auto;
		padding: 0 26px;
	}

	.shoppingcart-row__selections {
		display: flex;
		column-gap: 20px;

		@media #{$mq-medium-and-down} {
			display: block;
		}
	}

	.shoppingcart-header__create {
		margin: 0;
	}

	.shoppingcart-header__actions {
		margin-left: auto;
		align-items: end;
	}

	.shoppingcart-header__checkbox {
		margin-top: 6px;
		text-align: end;
	}

	:deep(.mydummen-order-overview__filter-related-customers) {
		display: block;

		label {
			display: block;
			font-size: math.div(14, 16)*1em;
			line-height: math.div(20, 14)*1em;
			padding: 5px 0;
			font-weight: 600;
		}

		.multiselect {
			min-width: 450px;

			&__tags {
				min-height: 38px;
				padding: 4px 40px 4px 8px;
				border: #ebebeb 3px solid;
			}

			&__single,
			&__placeholder {
				font-size: 16px;
				font-weight: 500;
				color: #595959;
				padding: 0;
				margin: 0;
				line-height: 24px;
			}

			input {
				margin: 0;
			}
		}
	}
</style>
